<template>
    <dialog-base 
        :show="show" 
        :dialog-title="$t('userProfile.title')"
        @close-modal="closeDialog"
        medium="medium"
    >
        <warning-panel-base 
            :form-warning="true"
            :text="warningPanelText"
            :show="showFormWarningPanel" 
            @close-warning-modal="closeWarningPanel"
        ></warning-panel-base>
        <div class="profile-content">
            <div class="user-info">
                <div class="user-name">
                    <strong>{{ $t('userProfile.username') }}:</strong> {{ username }}
                </div>
                <div class="support-info">
                    {{ $t('userProfile.supportInfo') }}
                </div>
            </div>
            <div class="form-fields">
                <div class="form-group">
                    <label>{{ $t('userProfile.email') }}</label>
                    <input 
                        type="email" 
                        v-model="email" 
                        :placeholder="$t('userProfile.emailPlaceholder')"
                        class="form-input"
                        :class="{ 'error': errors.email }"
                        @blur="errors.email = validateEmail(email)"
                    />
                    <div v-if="errors.email" class="error-message">{{ errors.email }}</div>
                </div>
                <div class="form-group">
                    <label>{{ $t('userProfile.phone') }}</label>
                    <input 
                        type="tel" 
                        v-model="phone" 
                        :placeholder="$t('userProfile.phonePlaceholder')"
                        class="form-input"
                        :class="{ 'error': errors.phone }"
                        @blur="errors.phone = validatePhone(phone)"
                    />
                    <div v-if="errors.phone" class="error-message">{{ errors.phone }}</div>
                </div>
                <div class="form-group">
                    <label class="radio-group-label">{{ $t('userProfile.verificationPreference') }}</label>
                    <div class="radio-group">
                        <label class="radio-option">
                            <input 
                                type="radio" 
                                v-model="verificationMethod" 
                                value="gsm"
                            />
                            <span>{{ $t('userProfile.gsm') }}</span>
                        </label>
                        <label class="radio-option">
                            <input 
                                type="radio" 
                                v-model="verificationMethod" 
                                value="email"
                            />
                            <span>{{ $t('userProfile.email') }}</span>
                        </label>
                    </div>
                </div>
            </div>
            <div class="dialog-footer">
                <button-base
                    medium="medium" 
                    button-type="button" 
                    class="footer_button" 
                    button-class="tertiary" 
                    :button-text="$t('userProfile.changePassword')" 
                    @click="navigateToResetPassword" 
                ></button-base>
                <button-base
                    xsmall="xsmall"
                    button-type="button" 
                    button-class="primary"
                    :button-text="isLoading ? $t('userProfile.saving') : $t('userProfile.save')"
                    :disabled="isLoading"
                    @click="saveProfile"
                ></button-base>
            </div>
        </div>
    </dialog-base>
</template>

<script>
import DialogBase from "../../ui/DialogBase.vue";
import WarningPanelBase from "../../ui/WarningPanelBase.vue";

export default {
    components: {
        DialogBase,
        WarningPanelBase
    },
    inject: [
        "warningPanelText",
        "showFormWarningPanel"
    ],
    props: [
        "show",
        "emailVal",
        "phoneVal",
        "verificationMethodVal"
    ],
    emits: [
        "close",
        "navigateToResetPassword"
    ],
    data() {
        return {
            email: "",
            phone: "",
            errors: {
                email: "",
                phone: ""
            },
            isLoading: false,
            verificationMethod: "",
        }
    },
    computed: {
        username() {
            return this.$store.getters.username;
        }
    },
    watch: {
        show(newVal) {
            if (newVal) this.$store.dispatch('clearError');
        },
        emailVal(newVal) {
            if (newVal) this.email = newVal;
        },
        phoneVal(newVal) {
            if (newVal) this.phone = newVal;
        },
        verificationMethodVal(newVal) {
            if (newVal) this.verificationMethod = newVal;
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close');
            this.clearErrors();
        },
        closeWarningPanel() {
            this.$store.dispatch('clearError');
        },
        validateEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (!email) {
                return this.$t('userProfile.emailRequired');
            }

            if (!emailRegex.test(email)) {
                return this.$t('userProfile.emailInvalid');
            }
            
            return '';
        },
        validatePhone(phone) {
            const phoneRegex = /^[0-9]{10,11}$/;

            if (!phone) {
                return this.$t('userProfile.phoneRequired');
            }

            const cleanPhone = phone.replace(/\s/g, '');

            if (!phoneRegex.test(cleanPhone)) {
                return this.$t('userProfile.phoneInvalid');
            }

            return "";
        },
        validateForm() {
            this.errors.email = this.validateEmail(this.email);
            this.errors.phone = this.validatePhone(this.phone);
            
            return !this.errors.email && !this.errors.phone;
        },
        clearErrors() {
            this.errors.email = "";
            this.errors.phone = "";
        },
        async saveProfile() {
            if (!this.validateForm()) return;

            this.isLoading = true;
            
            const profileData = {
                email: this.email,
                phone: this.phone,
                verificationMethod: this.verificationMethod
            };
            
            await this.$store.dispatch("updateProfile", profileData);
            
            this.$store.dispatch("throwFormError", { warningPanelText: this.$t("userProfile.profileSaved"), customError: true });
            
            setTimeout(() => {
                this.closeDialog();
            }, 2000);
            
            this.isLoading = false;
        },
        navigateToResetPassword() {
            this.$emit("navigateToResetPassword");
        }
    }
}
</script>

<style scoped>
.profile-content {
    padding: 20px;
}

.user-info {
    margin-bottom: 25px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
}

.user-name {
    font-size: 16px;
    margin-bottom: 8px;
    color: #333;
}

.support-info {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
}

.form-fields {
    margin-bottom: 25px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #333;
}

.form-input {
    width: 100%;
    font-size: 14px;
    padding: 10px 12px;
    border-radius: 4px;
    box-sizing: border-box;
    border: 1px solid #ddd;    
}

.form-input:focus {
    outline: none;
    border-color: var(--sniffer-color-primary);
    box-shadow: 0 0 0 2px rgba(var(--sniffer-color-primary-rgb), 0.2);
}

.radio-group-label {
    margin-bottom: 10px !important;
}

.radio-group {
    gap: 20px;
    display: flex;
}

.radio-option {
    display: flex;
    cursor: pointer;
    align-items: center;
    font-weight: normal !important;
}

.radio-option input[type="radio"] {
    margin-bottom: 0;
    margin-right: 8px;
}

.dialog-footer {
    display: flex;
    padding-top: 20px;
    align-items: center;
    border-top: 1px solid #eee;
    justify-content: end;
}

.footer_button {
    margin-right: 12px;
}

.form-input.error {
    border-color: #dc3545;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
}

.error-message {
    font-size: 13px;
    margin-top: 4px;
    color: #dc3545;
}
</style>
